/* Shared collection/search card previews. Does not control the buying-page gallery. */
(() => {
  if (customElements.get('mf-card-video')) return;
  let activePreview = null;

  class MFCardVideo extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.link = this.closest('.product-card__media');
      if (!this.link) return;

      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.wanted = false;
      this.attempt = (this.attempt || 0) + 1;
      this.link.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') this.start();
      }, options);
      this.link.addEventListener('pointerleave', () => this.stop(), options);
      this.link.addEventListener('pointercancel', () => this.stop(), options);
      // Never intercept navigation, including the first tap on a phone.
      this.link.addEventListener('click', () => this.stop(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
      }, options);
      window.addEventListener('pagehide', () => this.stop(), options);
      this.pointer.addEventListener('change', () => this.stop(), options);
      this.motion.addEventListener('change', () => this.stop(), options);
      navigator.connection?.addEventListener('change', () => this.stop(), options);

      this.observer?.disconnect();
      this.observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) this.stop();
      });
      this.observer.observe(this.link);
    }

    disconnectedCallback() {
      this.stop();
      this.abort?.abort();
      this.observer?.disconnect();
    }

    allowed() {
      return this.isConnected && this.pointer.matches && !this.motion.matches &&
        !navigator.connection?.saveData && !document.hidden;
    }

    prepareVideo() {
      if (this.video) return this.video;
      const source = this.querySelector('template')?.content.querySelector('video');
      if (!source) return null;
      const video = source.cloneNode(true);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.controls = false;
      video.tabIndex = -1;
      video.disablePictureInPicture = true;
      video.addEventListener('error', () => this.stop());
      video.addEventListener('playing', () => {
        if (this.wanted && this.allowed()) this.classList.add('is-playing');
        else this.stop();
      });
      this.append(video);
      this.video = video;
      return video;
    }

    async start() {
      if (!this.allowed()) return;
      if (activePreview && activePreview !== this) activePreview.stop();
      const video = this.prepareVideo();
      if (!video) return;
      activePreview = this;
      this.wanted = true;
      const attempt = ++this.attempt;
      try {
        await video.play();
        if (attempt !== this.attempt) return;
        if (this.wanted && this.allowed()) this.classList.add('is-playing');
        else this.stop();
      } catch {
        // Browser autoplay restrictions or failed media leave the normal image/link intact.
        if (attempt === this.attempt) this.stop();
      }
    }

    stop() {
      this.wanted = false;
      this.attempt = (this.attempt || 0) + 1;
      this.classList.remove('is-playing');
      if (this.video) {
        this.video.pause();
        try { this.video.currentTime = 0; } catch { /* Metadata may not yet be loaded. */ }
      }
      if (activePreview === this) activePreview = null;
    }
  }

  customElements.define('mf-card-video', MFCardVideo);
})();
